import React, { useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { Box, Typography, Chip } from "@mui/material";
import { DataGridPro } from "@mui/x-data-grid-pro";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import PaginationControl from "../../../table-component/PaginationControl";
import deviceEventStore from "../../../../stores/DeviceEventStore";
import MonthlyPicker from "./MonthlyPicker";

const renderBoldHeader = (params) => (
  <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
    {params?.colDef?.headerName || ""}
  </Typography>
);

const DeviceValve = observer(() => {
  const { pimios_id } = useParams();
  const deviceId = pimios_id?.split("-")[0];

  const [selectedMonth, setSelectedMonth] = useState(() =>
    new Date().toISOString().slice(0, 7)
  );
  const [pageV1, setPageV1] = useState(1);
  const [pageSizeV1, setPageSizeV1] = useState(10);
  const [pageV2, setPageV2] = useState(1);
  const [pageSizeV2, setPageSizeV2] = useState(10);

  const currentMonth = deviceEventStore.getMonthKey(`${selectedMonth}-01`);

  console.log("Current month: ", currentMonth);
  const valveEventsRaw = deviceEventStore.getValveEvents(
    deviceId,
    currentMonth
  );

  console.log("Valve event raws: ", valveEventsRaw);

  const v1Events = Array.isArray(valveEventsRaw?.v1) ? valveEventsRaw.v1 : [];
  const v2Events = Array.isArray(valveEventsRaw?.v2) ? valveEventsRaw.v2 : [];

  const columns = useMemo(
    () => [
      {
        field: "timestamp",
        headerName: "Timestamp",
        flex: 1,
        minWidth: 220,
        renderHeader: renderBoldHeader,
        renderCell: (params) => {
          const dateStr = new Date(params.value).toLocaleString();
          return (
            <Typography sx={{ fontSize: "0.875rem" }}>{dateStr}</Typography>
          );
        },
      },
      {
        field: "state",
        headerName: "State",
        flex: 1,
        minWidth: 160,
        renderHeader: renderBoldHeader,
        renderCell: (params) => {
          const isOpened = params.value === "opened";

          const config = {
            label: isOpened ? "Opened" : "Closed",
            icon: isOpened ? (
              <CheckCircleOutlinedIcon
                sx={{ color: "#fff", fontSize: "1rem" }}
              />
            ) : (
              <CancelOutlinedIcon sx={{ color: "#fff", fontSize: "1rem" }} />
            ),
            bgColor: isOpened ? "#16a34a" : "#dc2626", // green vs red
            fontWeight: 700,
            textColor: "#fff",
          };

          return (
            <Chip
              icon={
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    pl: "2px",
                  }}
                >
                  {config.icon}
                </Box>
              }
              label={
                <Typography
                  sx={{
                    fontWeight: config.fontWeight,
                    fontSize: "0.8rem",
                    textTransform: "none",
                  }}
                >
                  {config.label}
                </Typography>
              }
              sx={{
                minWidth: 150,
                px: 1.5,
                py: 0.5,
                height: "auto",
                borderRadius: "999px",
                backgroundColor: config.bgColor,
                color: config.textColor,
                textTransform: "none",
              }}
            />
          );
        },
      },
      {
        field: "source_log_id",
        headerName: "Source Log ID",
        flex: 1,
        minWidth: 220,
        renderHeader: renderBoldHeader,
      },
    ],
    []
  );

  const rowsV1 = useMemo(() => {
    return v1Events.map((e, idx) => ({
      id: e._id?.$oid || idx,
      timestamp: e.timestamp?.$date || e.timestamp,
      valve: e.payload?.valve || e.metadata?.valve,
      state: e.payload?.state || e.metadata?.position,
      source_log_id: e.payload?.metadata?.source_log_id,
    }));
  }, [v1Events]);

  const rowsV2 = useMemo(() => {
    return v2Events.map((e, idx) => ({
      id: e._id?.$oid || idx,
      timestamp: e.timestamp?.$date || e.timestamp,
      valve: e.payload?.valve || e.metadata?.valve,
      state: e.payload?.state || e.metadata?.position,
      source_log_id: e.payload?.metadata?.source_log_id,
    }));
  }, [v2Events]);

  const paginatedRowsV1 = rowsV1.slice(
    (pageV1 - 1) * pageSizeV1,
    pageV1 * pageSizeV1
  );
  const paginatedRowsV2 = rowsV2.slice(
    (pageV2 - 1) * pageSizeV2,
    pageV2 * pageSizeV2
  );

  const latestV1State = useMemo(() => {
    if (rowsV1.length === 0) return null;
    const sorted = [...rowsV1].sort(
      (a, b) => new Date(b.timestamp) - new Date(a.timestamp)
    );
    return sorted[0].state;
  }, [rowsV1]);

  const latestV2State = useMemo(() => {
    if (rowsV2.length === 0) return null;
    const sorted = [...rowsV2].sort(
      (a, b) => new Date(b.timestamp) - new Date(a.timestamp)
    );
    return sorted[0].state;
  }, [rowsV2]);

  return (
    <>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        <MonthlyPicker
          value={selectedMonth}
          onChange={setSelectedMonth}
          label="Month"
        />
      </Box>
      <Box sx={{ py: 2 }}>
        <Typography
          variant="h6"
          sx={{ fontWeight: "bold", mb: 1, textAlign: "center" }}
        >
          Valve 1 Events – Latest State:{" "}
          <Box
            component="span"
            sx={{ color: latestV1State === "opened" ? "green" : "red" }}
          >
            {latestV1State || "N/A"}
          </Box>
        </Typography>
        <Box sx={{ height: 540, width: "100%" }}>
          <DataGridPro
            rows={paginatedRowsV1}
            columns={columns}
            disableColumnMenu
            disableSelectionOnClick
            pagination={false}
            hideFooter={true}
            sx={{
              borderRadius: 2,
              boxShadow: 2,
              border: "1px solid #e0e0e0",
              backgroundColor: "#fafafa",
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#f4f6f8",
                fontWeight: "bold",
                fontSize: "0.9rem",
                borderBottom: "1px solid #ddd",
              },
              "& .MuiDataGrid-cell": {
                fontSize: "0.875rem",
                color: "#333",
              },
              "& .MuiDataGrid-row:nth-of-type(odd)": {
                backgroundColor: "#ffffff",
              },
              "& .MuiDataGrid-row:nth-of-type(even)": {
                backgroundColor: "#f7fbff",
              },
              "& .MuiDataGrid-row:hover": {
                backgroundColor: "#e3f2fd",
              },
              "& .MuiDataGrid-cell:focus": {
                outline: "none",
              },
            }}
          />
        </Box>
        <PaginationControl
          page={pageV1}
          setPage={setPageV1}
          totalPages={Math.ceil(rowsV1.length / pageSizeV1)}
          pageSize={pageSizeV1}
          setPageSize={setPageSizeV1}
        />

        <Box mt={10}>
          <Typography
            variant="h6"
            sx={{ fontWeight: "bold", mb: 1, textAlign: "center" }}
          >
            Valve 2 Events – Latest State:{" "}
            <Box
              component="span"
              sx={{ color: latestV2State === "opened" ? "green" : "red" }}
            >
              {latestV2State || "N/A"}
            </Box>
          </Typography>
          <Box sx={{ height: 540, width: "100%" }}>
            <DataGridPro
              rows={paginatedRowsV2}
              columns={columns}
              disableColumnMenu
              disableSelectionOnClick
              pagination={false}
              hideFooter={true}
              sx={{
                borderRadius: 2,
                boxShadow: 2,
                border: "1px solid #e0e0e0",
                backgroundColor: "#fafafa",
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: "#f4f6f8",
                  fontWeight: "bold",
                  fontSize: "0.9rem",
                  borderBottom: "1px solid #ddd",
                },
                "& .MuiDataGrid-cell": {
                  fontSize: "0.875rem",
                  color: "#333",
                },
                "& .MuiDataGrid-row:nth-of-type(odd)": {
                  backgroundColor: "#ffffff",
                },
                "& .MuiDataGrid-row:nth-of-type(even)": {
                  backgroundColor: "#f7fbff",
                },
                "& .MuiDataGrid-row:hover": {
                  backgroundColor: "#e3f2fd",
                },
                "& .MuiDataGrid-cell:focus": {
                  outline: "none",
                },
              }}
            />
          </Box>
          <PaginationControl
            page={pageV2}
            setPage={setPageV2}
            totalPages={Math.ceil(rowsV2.length / pageSizeV2)}
            pageSize={pageSizeV2}
            setPageSize={setPageSizeV2}
          />
        </Box>
      </Box>
    </>
  );
});

export default DeviceValve;
